class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
      this.setupRotatingPlaceholder();
    }
  }

  disconnectedCallback() {
    this.clearPlaceholderTimers();
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }

  setupRotatingPlaceholder() {
    if (this.input.dataset.rotatingPlaceholder !== 'true') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const rawList = this.input.dataset.placeholderList || '';
    const placeholderPrefix = this.input.dataset.placeholderPrefix || '';
    const placeholders = rawList
      .split('|')
      .map((item) => item.trim())
      .filter((item) => item.length > 0);

    if (placeholders.length === 0) return;

    const readInt = (key, fallback) => {
      const value = parseInt(this.input.dataset[key], 10);
      return Number.isFinite(value) ? value : fallback;
    };

    this.placeholderList = placeholders;
    this.placeholderPrefix = placeholderPrefix;
    this.placeholderIndex = 0;
    this.placeholderCharIndex = 0;
    this.placeholderDeleting = false;
    this.placeholderPaused = false;
    this.placeholderHasTypedOnce = false;
    this.placeholderConfig = {
      typingSpeed: readInt('typingSpeed', 100),
      deletingSpeed: readInt('deletingSpeed', 60),
      delayAfterWordTyped: readInt('delayAfterWordTyped', 2400),
      delayAfterDeleting: readInt('delayAfterDeleting', 500),
      delayBeforeFirstDelete: readInt('delayBeforeFirstDelete', 2000),
    };

    this.input.addEventListener('input', () => {
      if (this.input.value.length > 0) {
        this.pausePlaceholderRotation();
      } else {
        this.resumePlaceholderRotation();
      }
    });

    if (!this.input.value) {
    this.input.placeholder = this.placeholderPrefix;
    this.tickPlaceholder();
  }
  }

  pausePlaceholderRotation() {
    this.placeholderPaused = true;
    this.clearPlaceholderTimers();
  }

  resumePlaceholderRotation() {
    if (!this.placeholderList || this.input.value.length > 0) return;
    if (!this.placeholderPaused) return;
    this.placeholderPaused = false;
    this.tickPlaceholder();
  }

  clearPlaceholderTimers() {
    if (this.placeholderTimeout) {
      clearTimeout(this.placeholderTimeout);
      this.placeholderTimeout = null;
    }
  }

  queuePlaceholderTick(delay) {
    this.clearPlaceholderTimers();
    this.placeholderTimeout = setTimeout(() => this.tickPlaceholder(), delay);
  }

  tickPlaceholder() {
    if (this.placeholderPaused || !this.placeholderList) return;
    const currentPlaceholder = this.placeholderList[this.placeholderIndex];

    if (!this.placeholderDeleting) {
      this.placeholderCharIndex += 1;
      this.input.placeholder = `${this.placeholderPrefix}${currentPlaceholder.slice(0, this.placeholderCharIndex)}`;

      if (this.placeholderCharIndex >= currentPlaceholder.length) {
        const delay = this.placeholderHasTypedOnce
          ? this.placeholderConfig.delayAfterWordTyped
          : this.placeholderConfig.delayBeforeFirstDelete;
        this.placeholderHasTypedOnce = true;
        this.placeholderDeleting = true;
        this.queuePlaceholderTick(delay);
        return;
      }

      this.queuePlaceholderTick(this.placeholderConfig.typingSpeed);
      return;
    }

    this.placeholderCharIndex -= 1;
    const nextValue = currentPlaceholder.slice(0, Math.max(0, this.placeholderCharIndex));
    this.input.placeholder = `${this.placeholderPrefix}${nextValue}`;

    if (this.placeholderCharIndex <= 0) {
      this.placeholderDeleting = false;
      this.placeholderIndex = (this.placeholderIndex + 1) % this.placeholderList.length;
      this.queuePlaceholderTick(this.placeholderConfig.delayAfterDeleting);
      return;
    }

    this.queuePlaceholderTick(this.placeholderConfig.deletingSpeed);
  }
}

customElements.define('search-form', SearchForm);
